/**
 * Shared utilities for property components — both interior pages and listings.
 */

import { propertySummaries } from '../vibesites-company-data';
import { loadProperty, type DataLoaderConfig } from '@vibesites/runtime';

// ── Property data fetching ───────────────────────────────────────────────

/**
 * Fetch full property data. In preview environments the user's auth token
 * is available in localStorage (shared origin with the editor), so we call
 * the Multihub GraphQL API directly — mirroring preview/src/hooks/useProperty.ts.
 * In deployed builds (no token) we fall back to the baked JSON sidecar files.
 */
export async function fetchPropertyDetail(
  slug: string,
  propertyId?: string,
): Promise<any | null> {
  const apiToken = localStorage.getItem('vibesites-api-token');
  const apiUrl = (import.meta as any).env?.MULTIHUB_API_URL;

  if (apiToken && apiUrl && propertyId) {
    try {
      const config: DataLoaderConfig = { endpoint: apiUrl, token: apiToken, propertyId };
      return await loadProperty(config);
    } catch { /* fall through to static JSON */ }
  }

  return fetch(`/data/properties/${slug}.json`)
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null);
}

// ── General helpers ──────────────────────────────────────────────────────

export function slugify(str: string | null | undefined): string {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function getPropertyBasePath(property: Record<string, any>): string {
  const stateSlug = property._stateSlug || slugify(property.property_address_state);
  const citySlug = property._citySlug || slugify(property.property_address_city);
  const propSlug = property._slug || property.property_slug || slugify(property.property_name);
  return `/apartments/${stateSlug}/${citySlug}/${propSlug}`;
}

export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

export function phoneDigits(phone: string | null | undefined): string {
  return (phone || '').replace(/\D/g, '');
}

const PLACEHOLDER_LINK_RE = /^(https?:\/\/)?(www\.)?pending\.com\/?$/i;

/**
 * Normalize a property link field, returning '' for empty values and the
 * www.pending.com placeholder so the link can be hidden.
 */
export function cleanLinkUrl(value: string | null | undefined): string {
  const trimmed = (value || '').trim();
  return PLACEHOLDER_LINK_RE.test(trimmed) ? '' : trimmed;
}

// ── Special offer helpers ────────────────────────────────────────────────

export interface PropertySpecialOffer {
  title: string;
  description: string;
  disclaimer: string;
}

export interface RawSpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | boolean | null;
  end_date?: string | boolean | null;
  [key: string]: unknown;
}

function cleanOfferText(value: string | boolean | null | undefined): string {
  if (typeof value === 'boolean' || value == null) return '';
  const trimmed = String(value).trim();
  if (trimmed === 'false' || trimmed === 'true') return '';
  return trimmed;
}

/**
 * Parse an offer start/end date string. Feed values like "", "false", "true",
 * or booleans are treated as "unset" and return null.
 *
 * ISO-like strings without a timezone (e.g. "2026-04-30T03:00:00") are parsed
 * by `new Date()` as the viewer's LOCAL time — that's the localized behavior
 * we want so an offer ending at 3am on April 30 ends at 3am wherever the user
 * is viewing from.
 */
function parseOfferDate(value: string | boolean | null | undefined): Date | null {
  const text = cleanOfferText(value);
  if (!text) return null;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * An offer is considered active when the current local time falls within its
 * [start_date, end_date] window. Missing/unset bounds are treated as open
 * ended, so an offer with neither date is always live.
 */
export function isOfferActive(raw: RawSpecialOffer | null | undefined, now: Date = new Date()): boolean {
  const start = parseOfferDate(raw?.start_date);
  if (start && now < start) return false;
  const end = parseOfferDate(raw?.end_date);
  if (end && now > end) return false;
  return true;
}

function normalizeOffer(raw: RawSpecialOffer | null | undefined): PropertySpecialOffer | null {
  const title = cleanOfferText(raw?.title);
  const description = cleanOfferText(raw?.description);
  const disclaimer = cleanOfferText(raw?.disclaimer);
  if (!title && !description && !disclaimer) return null;
  return { title, description, disclaimer };
}

/**
 * Resolve the best available property special offers from either the
 * `special_offers` array or the legacy flat special-offer fields.
 *
 * Offers outside their start_date/end_date window are filtered out. Offers
 * with neither date set are treated as always-live.
 *
 * We intentionally ignore the upstream `enabled` flag because the current
 * feed frequently sets it to "false" even when live offer text is present.
 */
export function getPropertySpecialOffers(property: Record<string, any> | null | undefined): PropertySpecialOffer[] {
  if (!property) return [];

  const now = new Date();

  const arrayOffers = Array.isArray(property.special_offers)
    ? property.special_offers
        .filter((offer: RawSpecialOffer) => isOfferActive(offer, now))
        .map((offer: RawSpecialOffer) => normalizeOffer(offer))
        .filter(Boolean) as PropertySpecialOffer[]
    : [];
  if (arrayOffers.length > 0) return arrayOffers;

  const legacyRaw: RawSpecialOffer = {
    title: property.property_specialoffer_title,
    description: property.property_specialoffer_description,
    disclaimer: property.property_specialoffer_disclaimer,
    start_date: property.property_specialoffer_start_date,
    end_date: property.property_specialoffer_end_date,
  };
  if (!isOfferActive(legacyRaw, now)) return [];
  const legacyOffer = normalizeOffer(legacyRaw);
  return legacyOffer ? [legacyOffer] : [];
}

export function getPropertySpecialOffer(property: Record<string, any> | null | undefined): PropertySpecialOffer | null {
  return getPropertySpecialOffers(property)[0] || null;
}

/**
 * Filter + normalize raw special-offer items coming from a `special_offers`
 * collection binding (`useCollectionBinding`). Unlike `getPropertySpecialOffers`
 * this reads the live, editor-resolved collection array rather than the baked
 * `property` object, so offers update without a redeploy.
 *
 * Offers outside their start_date/end_date window are dropped. The upstream
 * `enabled` flag is intentionally ignored (the feed frequently leaves it
 * "false" even for live offers).
 */
export function normalizeSpecialOffers(rawOffers: RawSpecialOffer[] | null | undefined): PropertySpecialOffer[] {
  if (!Array.isArray(rawOffers)) return [];
  const now = new Date();
  return rawOffers
    .filter(offer => isOfferActive(offer, now))
    .map(offer => normalizeOffer(offer))
    .filter(Boolean) as PropertySpecialOffer[];
}

// ── Office hours sorting ─────────────────────────────────────────────────

const DAY_ORDER: Record<string, number> = {
  monday: 0, mon: 0,
  tuesday: 1, tue: 1, tues: 1,
  wednesday: 2, wed: 2,
  thursday: 3, thu: 3, thur: 3, thurs: 3,
  friday: 4, fri: 4,
  saturday: 5, sat: 5,
  sunday: 6, sun: 6,
};

function dayIndex(entry: { day?: string; label?: string }): number {
  const raw = (entry.label || entry.day || '').trim().toLowerCase();
  // Handle compound labels like "Monday - Friday" by keying on the first word
  const first = raw.split(/[\s\-–—]/)[0];
  return DAY_ORDER[first] ?? 99;
}

/**
 * Sort office-hour entries into Monday–Sunday order.
 * Entries whose day/label doesn't match a known weekday are appended at the end
 * in their original order.
 */
export function sortOfficeHours<T extends { day?: string; label?: string }>(hours: T[]): T[] {
  return [...hours].sort((a, b) => dayIndex(a) - dayIndex(b));
}

// ── Time formatting ──────────────────────────────────────────────────────

const TIME_24H_RE = /^(\d{1,2}):(\d{2})$/;

/**
 * Detect whether a time string like "17:30" is in 24-hour format and convert
 * it to 12-hour AM/PM (e.g. "5:30 PM"). Already-12-hour strings (containing
 * "am"/"pm") are returned as-is.
 */
export function formatTime24to12(time: string | undefined | null): string {
  if (!time) return '';
  const trimmed = time.trim();
  if (/[ap]\.?m\.?/i.test(trimmed)) return trimmed;

  const match = trimmed.match(TIME_24H_RE);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  if (hours < 0 || hours > 23) return trimmed;

  const suffix = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  return `${hours}:${minutes} ${suffix}`;
}

/**
 * Format an hours range string (e.g. "08:30 - 17:30") by converting any
 * 24-hour times it contains to 12-hour AM/PM format. Already-formatted
 * ranges or non-matching strings are returned unchanged.
 */
export function formatHoursRange(hoursStr: string | undefined | null): string {
  if (!hoursStr) return '';
  return hoursStr
    .split(/(\s*[-–—]\s*)/)
    .map(part => {
      if (/^[\s\-–—]+$/.test(part)) return part;
      return formatTime24to12(part);
    })
    .join('');
}

// ── State name lookups ───────────────────────────────────────────────────

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

export function getStateName(abbr: string): string {
  return STATE_NAMES[abbr?.toUpperCase()] || abbr;
}

export function normalizeAbbr(val: string): string {
  if (!val) return '';
  if (val.length <= 2) return val.toUpperCase();
  const upper = val.toUpperCase();
  for (const [abbr, name] of Object.entries(STATE_NAMES)) {
    if (name.toUpperCase() === upper) return abbr;
  }
  // Fallback: match if the input starts with a known state name (handles bad data like "Oklahoma City")
  for (const [abbr, name] of Object.entries(STATE_NAMES)) {
    if (upper.startsWith(name.toUpperCase())) return abbr;
  }
  return val;
}

// ── State/city index from propertySummaries ──────────────────────────────

export interface StateIndex {
  [stateSlug: string]: {
    slug: string;
    abbr: string;
    name: string;
    cities: {
      [citySlug: string]: {
        slug: string;
        name: string;
        properties: any[];
      };
    };
  };
}

let _cachedIndex: StateIndex | null = null;

/**
 * Build a state -> city -> properties index from propertySummaries.
 * Cached after first call since the data is static.
 */
export function getPropertiesByState(): StateIndex {
  if (_cachedIndex) return _cachedIndex;

  const index: StateIndex = {};
  for (const prop of propertySummaries) {
    const { _stateSlug, _citySlug } = prop as any;
    if (!_stateSlug || !_citySlug) continue;
    const abbr = normalizeAbbr((prop as any).property_address_state || '');
    // Use the abbreviation-based slug when the original _stateSlug looks wrong
    // (e.g., "oklahoma-city" instead of "ok")
    const stateSlug = /^[a-z]{2}$/.test(_stateSlug) ? _stateSlug : slugify(abbr);
    if (!stateSlug) continue;
    if (!index[stateSlug]) {
      index[stateSlug] = { slug: stateSlug, abbr, name: getStateName(abbr), cities: {} };
    }
    if (!index[stateSlug].cities[_citySlug]) {
      index[stateSlug].cities[_citySlug] = { slug: _citySlug, name: (prop as any).property_address_city || _citySlug, properties: [] };
    }
    index[stateSlug].cities[_citySlug].properties.push(prop);
  }
  _cachedIndex = index;
  return index;
}

// ── Market grouping ──────────────────────────────────────────────────────
// Maps a canonical city slug to the set of city slugs that should appear
// on that market page. Entries here represent curated metro areas where
// nearby suburbs are marketed under a single umbrella city page.
// Cities not listed fall back to strict single-city behavior.

export interface MarketGrouping {
  [stateSlug: string]: {
    [canonicalCitySlug: string]: string[];
  };
}

export const MARKET_GROUPS: MarketGrouping = {
  ar: {
    'little-rock': ['little-rock', 'north-little-rock'],
  },
  mo: {
    'kansas-city': ['kansas-city', 'belton', 'independence'],
  },
  ok: {
    'oklahoma-city': ['oklahoma-city', 'norman', 'edmond', 'moore', 'yukon'],
    'tulsa': ['tulsa', 'broken-arrow', 'jenks', 'owasso'],
  },
  tx: {
    'waco': ['waco', 'temple', 'hewitt'],
  },
  ks: {
    'wichita': ['wichita', 'maize'],
  },
};

/**
 * Given a state slug and city slug, return the full list of city slugs
 * that should be included on that market page. Returns a single-element
 * array with the input city slug when no grouping is configured.
 */
export function getMarketCitySlugs(stateSlug: string, citySlug: string): string[] {
  return MARKET_GROUPS[stateSlug]?.[citySlug] || [citySlug];
}

/**
 * Collect all properties from the state index that belong to any of the
 * given city slugs within a state.
 */
export function getMarketProperties(stateSlug: string, citySlugs: string[]): any[] {
  const index = getPropertiesByState();
  const stateData = index[stateSlug];
  if (!stateData) return [];
  return citySlugs.flatMap(slug => stateData.cities[slug]?.properties || []);
}

export { propertySummaries };
